// Modal para configurar/limpiar la recurrencia de una tarea-plantilla.
// Aparece desde un botón 🔁 en cada fila (admin only). Al guardar, el
// frontend manda un PATCH normal de la tarea con `recurrencia: {...}` (o
// `null` para limpiar) y el backend cron se encarga de la instanciación
// diaria. La tarea original sigue visible en la tabla con un tag para
// recordar al admin que es la plantilla.

import React, { useState, useEffect } from "react";
import {
  Modal,
  Select,
  InputNumber,
  DatePicker,
  Button,
  Alert,
  Switch,
  Form,
  Typography,
} from "antd";
import dayjs from "dayjs";
import type { Recurrencia, RecurrenciaTipo } from "../types";
import { ahoraChile } from "../../../utils/fechaChile";

interface RecurrenciaModalProps {
  open: boolean;
  onClose: () => void;
  current: Recurrencia | null | undefined;
  onSave: (next: Recurrencia | null) => Promise<void> | void;
  saving?: boolean;
}

const TIPO_OPTIONS: { value: RecurrenciaTipo; label: string }[] = [
  { value: "diaria", label: "Diaria" },
  { value: "semanal", label: "Semanal" },
  { value: "mensual", label: "Mensual" },
];

const RecurrenciaModal: React.FC<RecurrenciaModalProps> = ({
  open,
  onClose,
  current,
  onSave,
  saving,
}) => {
  // Toggle "es recurrente" Sí/No. Por default OFF — la recurrencia es una
  // OPCIÓN; las tareas normales no son recurrentes. Cuando el usuario la
  // prende, se habilitan los demás campos. Al guardar:
  //  - enabled=false → onSave(null)  (limpia recurrencia si la tenía).
  //  - enabled=true  → onSave({ tipo, cada, proximaEl }).
  const [enabled, setEnabled] = useState<boolean>(false);
  const [tipo, setTipo] = useState<RecurrenciaTipo>("semanal");
  const [cada, setCada] = useState<number>(1);
  const [proximaEl, setProximaEl] = useState<dayjs.Dayjs | null>(null);

  // Re-inicializa cada vez que se abre el modal o cambia la tarea
  // editada — evita arrastrar el draft de una tarea anterior.
  useEffect(() => {
    if (!open) return;
    if (current) {
      setEnabled(true);
      setTipo(current.tipo);
      setCada(current.cada || 1);
      setProximaEl(current.proximaEl ? dayjs(current.proximaEl) : ahoraChile());
    } else {
      setEnabled(false);
      setTipo("semanal");
      setCada(1);
      setProximaEl(ahoraChile().add(1, "week"));
    }
  }, [open, current]);

  const handleSave = async () => {
    if (!enabled) {
      // Toggle apagado: si la tarea tenía recurrencia se limpia; si no la
      // tenía es no-op (mismo efecto que cancelar).
      await onSave(null);
      return;
    }
    if (!proximaEl) return;
    const next: Recurrencia = {
      tipo,
      cada: Math.max(1, Number(cada || 1)),
      proximaEl: proximaEl.format("YYYY-MM-DD"),
    };
    await onSave(next);
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Configurar recurrencia"
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          loading={saving}
          disabled={enabled && !proximaEl}
        >
          Guardar
        </Button>,
      ]}
    >
      {/*
        Toggle prominente Sí/No — la recurrencia es una OPCIÓN. Por
        default está apagado (las tareas normales no son recurrentes).
        Cuando se prende, se habilitan los campos de configuración.
      */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: 12,
          background: enabled
            ? "var(--violet-100)"
            : "var(--color-surface-secondary)",
          border: "1px solid var(--border-color)",
          borderRadius: "var(--radius-md)",
          marginBottom: 16,
          transition: "background 0.2s",
        }}
      >
        <Switch checked={enabled} onChange={setEnabled} />
        <div>
          <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>
            ¿Es una tarea recurrente?
          </div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {enabled
              ? "Sí — esta tarea se generará automáticamente cada cierto tiempo."
              : "No — esta tarea es de una sola vez."}
          </Typography.Text>
        </div>
      </div>

      {enabled && (
        <Alert
          type="info"
          showIcon
          message="Plantilla recurrente"
          description="Esta tarea funcionará como plantilla: cada vez que llegue la fecha de próxima ejecución se creará automáticamente una copia (sin recurrencia). La plantilla se mantiene en la tabla con un ícono de recurrencia."
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        layout="vertical"
        style={{
          opacity: enabled ? 1 : 0.5,
          pointerEvents: enabled ? "auto" : "none",
        }}
      >
        <Form.Item label="Frecuencia" style={{ marginBottom: 16 }}>
          <Select
            value={tipo}
            onChange={(v) => setTipo(v)}
            options={TIPO_OPTIONS}
            style={{ width: "100%" }}
            disabled={!enabled}
          />
        </Form.Item>

        <Form.Item label="Cada" style={{ marginBottom: 16 }}>
          <InputNumber
            min={1}
            max={365}
            value={cada}
            onChange={(v) => setCada(Number(v ?? 1))}
            style={{ width: "100%" }}
            disabled={!enabled}
            addonAfter={
              tipo === "diaria"
                ? cada === 1
                  ? "día"
                  : "días"
                : tipo === "semanal"
                  ? cada === 1
                    ? "semana"
                    : "semanas"
                  : cada === 1
                    ? "mes"
                    : "meses"
            }
          />
        </Form.Item>

        <Form.Item
          label="Próxima ejecución"
          style={{ marginBottom: 4 }}
          extra="Cuando llegue esta fecha, se generará una copia de la tarea y la siguiente ejecución se calculará automáticamente. También aparecerá un recordatorio en la barra de alertas."
        >
          <DatePicker
            value={proximaEl}
            onChange={(d) => setProximaEl(d)}
            format="DD/MM/YYYY"
            style={{ width: "100%" }}
            allowClear={false}
            disabled={!enabled}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default RecurrenciaModal;
